import { useState } from 'react';
import { Badge, Button, Checkbox, FormField, Input, Modal, Select, Spinner, Table } from '@vwfs-bronson/bronson-react';
import {
  AuthTokens,
} from '../../auth';
import {
  CreateModelRequest,
  FesConfig,
  ModelDefinition,
  ModelStatus,
  ReasoningEffort,
  UpdateModelRequest,
  createModel,
  updateModel,
} from '../../fes';
import { RowActionsMenu } from '../RowActionsMenu';

const effortOptions: ReasoningEffort[] = ['minimal', 'low', 'medium', 'high', 'xhigh'];
const providerOptions = ['openai', 'azure', 'azure-openai', 'anthropic', 'google', 'mistral', 'bedrock'];
type ContextProfile = 'codex-default' | '272k' | '400k' | '1050k' | 'custom';

const contextProfiles: Array<{
  value: Exclude<ContextProfile, 'custom'>;
  label: string;
  contextWindowTokens?: number;
  autoCompactTokenLimit?: number;
}> = [
  {
    value: 'codex-default',
    label: 'Codex default',
  },
  {
    value: '272k',
    label: '272K',
    contextWindowTokens: 272000,
    autoCompactTokenLimit: 240000,
  },
  {
    value: '400k',
    label: '400K',
    contextWindowTokens: 400000,
    autoCompactTokenLimit: 360000,
  },
  {
    value: '1050k',
    label: '1M',
    contextWindowTokens: 1050000,
    autoCompactTokenLimit: 900000,
  },
];

function optionalInteger(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

function profileForLimits(contextWindowTokens?: number, autoCompactTokenLimit?: number): ContextProfile {
  if (contextWindowTokens === undefined && autoCompactTokenLimit === undefined) return 'codex-default';
  return contextProfiles.find(profile =>
    profile.contextWindowTokens === contextWindowTokens
    && profile.autoCompactTokenLimit === autoCompactTokenLimit
  )?.value ?? 'custom';
}

function recommendedProfileForModel(model: string): ContextProfile | undefined {
  const normalized = model.trim().toLowerCase();
  if (/gpt-5\.(4|5|6)/.test(normalized)) return '1050k';
  if (/gpt-5\.2/.test(normalized)) return '400k';
  return undefined;
}

function formatContextWindow(contextWindowTokens?: number): string {
  if (contextWindowTokens === 1050000) return '1M';
  if (contextWindowTokens === 400000) return '400K';
  if (contextWindowTokens === 272000) return '272K';
  return contextWindowTokens?.toLocaleString('en-US') ?? 'Codex default';
}

const emptyDraft: CreateModelRequest = {
  provider: 'azure',
  model: '',
  displayName: '',
  supportedReasoningEfforts: ['low', 'medium', 'high'],
  defaultReasoningEffort: 'medium',
  status: 'active',
};

export function ModelsView({
  canEdit = true,
  fesConfig,
  models,
  onModelSaved,
  tokens,
}: {
  canEdit?: boolean;
  fesConfig: FesConfig;
  models: ModelDefinition[];
  onModelSaved: (model: ModelDefinition) => void;
  tokens: AuthTokens;
}) {
  const [editing, setEditing] = useState<ModelDefinition | null>(null);
  const [draft, setDraft] = useState<CreateModelRequest | null>(null);
  const [contextProfile, setContextProfile] = useState<ContextProfile>('codex-default');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const creating = Boolean(draft && !editing);

  const openCreate = () => {
    setEditing(null);
    setContextProfile('codex-default');
    setDraft({ ...emptyDraft, supportedReasoningEfforts: [...emptyDraft.supportedReasoningEfforts] });
    setError(undefined);
  };

  const openEdit = (model: ModelDefinition) => {
    setEditing(model);
    setContextProfile(profileForLimits(model.contextWindowTokens, model.autoCompactTokenLimit));
    setDraft({
      provider: model.provider,
      model: model.model,
      displayName: model.displayName,
      supportedReasoningEfforts: [...model.supportedReasoningEfforts],
      defaultReasoningEffort: model.defaultReasoningEffort,
      contextWindowTokens: model.contextWindowTokens,
      autoCompactTokenLimit: model.autoCompactTokenLimit,
      status: model.status,
    });
    setError(undefined);
  };

  const close = () => {
    if (saving) return;
    setEditing(null);
    setDraft(null);
    setContextProfile('codex-default');
    setError(undefined);
  };

  const changeContextProfile = (profile: ContextProfile) => {
    if (!draft) return;
    setContextProfile(profile);
    if (profile === 'custom') return;
    const preset = contextProfiles.find(candidate => candidate.value === profile);
    setDraft({
      ...draft,
      contextWindowTokens: preset?.contextWindowTokens,
      autoCompactTokenLimit: preset?.autoCompactTokenLimit,
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(undefined);
    try {
      const update: UpdateModelRequest = {
        ...draft,
        contextWindowTokens: draft.contextWindowTokens ?? null,
        autoCompactTokenLimit: draft.autoCompactTokenLimit ?? null,
      };
      const response = editing
        ? await updateModel(fesConfig, tokens, editing.modelId, update)
        : await createModel(fesConfig, tokens, draft);
      if (!response.data) throw new Error('Model API returned no model.');
      onModelSaved(response.data);
      setEditing(null);
      setDraft(null);
      setContextProfile('codex-default');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save model.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (model: ModelDefinition) => {
    setSaving(true);
    setError(undefined);
    try {
      const status: ModelStatus = model.status === 'active' ? 'inactive' : 'active';
      const response = await updateModel(fesConfig, tokens, model.modelId, { status });
      if (!response.data) throw new Error('Model API returned no model.');
      onModelSaved(response.data);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to update model status.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEffort = (effort: ReasoningEffort, checked: boolean) => {
    if (!draft) return;
    const supported = checked
      ? Array.from(new Set([...draft.supportedReasoningEfforts, effort]))
      : draft.supportedReasoningEfforts.filter(value => value !== effort);
    setDraft({
      ...draft,
      supportedReasoningEfforts: supported,
      defaultReasoningEffort: supported.includes(draft.defaultReasoningEffort)
        ? draft.defaultReasoningEffort
        : supported[0] ?? draft.defaultReasoningEffort,
    });
  };

  const canSave = Boolean(
    draft?.provider
    && draft.model.trim()
    && draft.displayName.trim()
    && draft.supportedReasoningEfforts.length
    && draft.supportedReasoningEfforts.includes(draft.defaultReasoningEffort)
    && (draft.contextWindowTokens === undefined
      || (Number.isInteger(draft.contextWindowTokens) && draft.contextWindowTokens > 0))
    && (draft.autoCompactTokenLimit === undefined
      || (Number.isInteger(draft.autoCompactTokenLimit)
        && draft.autoCompactTokenLimit > 0
        && draft.contextWindowTokens !== undefined
        && draft.autoCompactTokenLimit < draft.contextWindowTokens))
    && !saving
  );
  const recommendedContextProfile = draft ? recommendedProfileForModel(draft.model) : undefined;
  const selectedContextPreset = contextProfiles.find(profile => profile.value === contextProfile);

  return (
    <>
      {canEdit ? (
        <div className="app-table-toolbar">
          <Button icon="semantic-plus" onClick={openCreate}>New Model</Button>
        </div>
      ) : null}
      {error && !draft ? <div className="app-result app-team-error" role="alert">{error}</div> : null}
      {models.length === 0 ? <p className="app-empty">No models configured.</p> : (
        <div className="app-table-wrap">
          <Table className="app-table" colored>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Model</Table.Th>
                <Table.Th>Context</Table.Th>
                <Table.Th>Efforts</Table.Th>
                <Table.Th>Status</Table.Th>
                {canEdit ? <Table.Th className="app-table-actions-column">Actions</Table.Th> : null}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {models.map(model => (
                <Table.Tr key={model.modelId}>
                  <Table.Td>{model.displayName}</Table.Td>
                  <Table.Td>{model.provider}</Table.Td>
                  <Table.Td>{model.model}</Table.Td>
                  <Table.Td>{formatContextWindow(model.contextWindowTokens)}</Table.Td>
                  <Table.Td>{model.supportedReasoningEfforts.join(', ')}</Table.Td>
                  <Table.Td>
                    <Badge
                      label={model.status === 'active' ? 'Active' : 'Inactive'}
                      modifier={model.status === 'active' ? 'success' : undefined}
                    />
                  </Table.Td>
                  {canEdit ? (
                    <Table.Td className="app-table-actions-column">
                      <RowActionsMenu
                        disabled={saving}
                        label={`model ${model.displayName}`}
                        items={[
                          {
                            icon: 'semantic-edit',
                            label: 'Edit',
                            onSelect: () => openEdit(model),
                          },
                          {
                            icon: model.status === 'active' ? 'semantic-pause' : 'semantic-checkmark',
                            label: model.status === 'active' ? 'Deactivate' : 'Activate',
                            onSelect: () => void toggleStatus(model),
                          },
                        ]}
                        onDelete={() => undefined}
                        onEdit={() => undefined}
                      />
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
      {draft ? (
        <Modal
          buttonCancelLabel="Cancel model edit"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={creating ? 'Create model' : 'Save model'}
          buttonConfirmText={saving ? 'Saving...' : creating ? 'Create' : 'Save'}
          confirmationDisabled={!canSave}
          onCancel={close}
          onClose={close}
          onConfirm={() => void save()}
          shown
          large
          left
          status={error ? 'error' : undefined}
          title={creating ? 'Create model' : 'Edit model'}
        >
          <div className="app-agent-editor">
            {saving ? <Spinner center>Saving model...</Spinner> : null}
            {error ? <div className="app-result app-team-error" role="alert">{error}</div> : null}
            <div className="app-agent-editor-grid">
              <FormField labelForId="model-display-name" labelText="Display Name" notion type="input">
                <Input id="model-display-name" value={draft.displayName} onChange={event => setDraft({ ...draft, displayName: event.target.value })} />
              </FormField>
              <FormField labelForId="model-provider" labelText="Provider" notion type="select">
                <Select id="model-provider" value={draft.provider} onChange={event => setDraft({ ...draft, provider: event.target.value })}>
                  {providerOptions.map(provider => <Select.Item key={provider} value={provider}>{provider}</Select.Item>)}
                </Select>
              </FormField>
              <FormField labelForId="model-identifier" labelText="Model Identifier" notion type="input">
                <Input id="model-identifier" value={draft.model} onChange={event => setDraft({ ...draft, model: event.target.value })} />
              </FormField>
              <FormField labelForId="model-status" labelText="Status" type="select">
                <Select id="model-status" value={draft.status} onChange={event => setDraft({ ...draft, status: event.target.value as ModelStatus })}>
                  <Select.Item value="active">active</Select.Item>
                  <Select.Item value="inactive">inactive</Select.Item>
                </Select>
              </FormField>
              <FormField labelForId="model-context-profile" labelText="Context Window" notion type="select">
                <Select
                  id="model-context-profile"
                  value={contextProfile}
                  onChange={event => changeContextProfile(event.target.value as ContextProfile)}
                >
                  {contextProfiles.map(profile => (
                    <Select.Item key={profile.value} value={profile.value}>
                      {`${profile.label}${recommendedContextProfile === profile.value ? ' — recommended' : ''}`}
                    </Select.Item>
                  ))}
                  <Select.Item value="custom">Custom values (advanced)</Select.Item>
                </Select>
              </FormField>
              {contextProfile === 'custom' ? (
                <>
                  <FormField labelForId="model-context-window" labelText="Context Window Tokens" type="input">
                    <Input
                      id="model-context-window"
                      type="number"
                      min="1"
                      step="1"
                      value={draft.contextWindowTokens ?? ''}
                      onChange={event => setDraft({
                        ...draft,
                        contextWindowTokens: optionalInteger(event.target.value),
                      })}
                    />
                  </FormField>
                  <FormField labelForId="model-auto-compact" labelText="Auto-Compact Token Limit" type="input">
                    <Input
                      id="model-auto-compact"
                      type="number"
                      min="1"
                      step="1"
                      value={draft.autoCompactTokenLimit ?? ''}
                      onChange={event => setDraft({
                        ...draft,
                        autoCompactTokenLimit: optionalInteger(event.target.value),
                      })}
                    />
                  </FormField>
                </>
              ) : null}
              <FormField labelForId="model-default-effort" labelText="Default Reasoning Effort" notion type="select">
                <Select
                  id="model-default-effort"
                  value={draft.defaultReasoningEffort}
                  onChange={event => setDraft({ ...draft, defaultReasoningEffort: event.target.value as ReasoningEffort })}
                >
                  {draft.supportedReasoningEfforts.map(effort => <Select.Item key={effort} value={effort}>{effort}</Select.Item>)}
                </Select>
              </FormField>
            </div>
            <p className="app-field-hint">
              {recommendedContextProfile
                ? `The recommended profile for ${draft.model} is marked above.`
                : 'Use Codex default unless the provider documentation confirms a larger context window.'}
              {selectedContextPreset?.contextWindowTokens
                ? ` ${selectedContextPreset.label} uses ${selectedContextPreset.contextWindowTokens.toLocaleString('en-US')} tokens and compacts at ${selectedContextPreset.autoCompactTokenLimit?.toLocaleString('en-US')}.`
                : ' Presets configure a safe compaction threshold automatically.'}
            </p>
            <section className="app-agent-editor-section" aria-labelledby="model-efforts-heading">
              <h3 id="model-efforts-heading" className="app-agent-editor-section-title">Supported reasoning efforts</h3>
              <div className="app-actions">
                {effortOptions.map(effort => (
                  <Checkbox
                    key={effort}
                    id={`model-effort-${effort}`}
                    checked={draft.supportedReasoningEfforts.includes(effort)}
                    onChange={event => toggleEffort(effort, event.target.checked)}
                  >
                    {effort}
                  </Checkbox>
                ))}
              </div>
            </section>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
